import React, { useEffect, useRef, useState } from 'react';
import { OCR_LIMITS, startLocalOcr, validateOcrFile } from '../lib/localOcr';

/** Text of a copy only. Applying it elsewhere always requires an explicit caller action. */
export default function LocalOcrPanel({ onText, namespaceKey = 'local' }) {
  const [file, setFile] = useState(null), [text, setText] = useState('');
  const [busy, setBusy] = useState(false), [status, setStatus] = useState(''), [error, setError] = useState('');
  const operation = useRef(null), epoch = useRef(0), fileInput = useRef(null);
  useEffect(() => {
    epoch.current += 1; operation.current?.cancel(); operation.current = null;
    setFile(null); setText(''); setBusy(false); setStatus(''); setError('');
    if (fileInput.current) fileInput.current.value = '';
    return () => { epoch.current += 1; operation.current?.cancel(); operation.current = null; };
  }, [namespaceKey]);
  const cancel = () => {
    epoch.current += 1; operation.current?.cancel(); operation.current = null;
    setBusy(false); setStatus('Extraction annulée.');
  };
  const choose = event => {
    cancel(); setStatus(''); setText(''); setError('');
    const next = event.target.files?.[0];
    try { if (next) validateOcrFile(next); setFile(next || null); }
    catch (e) { setFile(null); setError(e.message); event.target.value = ''; }
  };
  const extract = async () => {
    if (!file || operation.current) return;
    if (text && !window.confirm('Remplacer le texte affiché par une nouvelle extraction ?')) return;
    const token = ++epoch.current;
    setBusy(true); setError(''); setStatus('Démarrage de l’extraction locale…');
    const job = startLocalOcr(file, { onProgress: ({ message }) => { if (epoch.current === token) setStatus(message); } });
    operation.current = job;
    try {
      const result = await job.promise;
      if (epoch.current !== token) return;
      setText(result.text);
      setStatus(`${result.pages} page${result.pages > 1 ? 's' : ''} traitée${result.pages > 1 ? 's' : ''}. ${result.method === 'pdf-text' ? 'Texte natif du PDF extrait.' : 'Reconnaissance optique effectuée.'} Relisez le résultat avant utilisation.`);
    } catch (e) {
      if (epoch.current === token && e.name !== 'AbortError') { setError(e.message || 'Extraction impossible.'); setStatus(''); }
    } finally {
      if (epoch.current === token) { setBusy(false); operation.current = null; }
    }
  };
  const copy = async () => {
    const token = epoch.current;
    try { await navigator.clipboard.writeText(text); if (token === epoch.current) setStatus('Texte copié.'); }
    catch { if (token === epoch.current) setError('Copie indisponible. Sélectionnez le texte et utilisez le raccourci de copie.'); }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = 'texte-copie-relu.txt'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return <section className="studio-card" aria-labelledby="local-ocr-title">
    <h2 id="local-ocr-title">Extraire le texte d’une copie</h2>
    <p>Traitement sur cet appareil, sans API et sans envoi du fichier. Les ressources du moteur sont chargées depuis ce site. Adapté aux textes imprimés en français ; la reconnaissance des écritures manuscrites n’est pas garantie.</p>
    <p>PNG, JPEG, WebP ou PDF · 10 Mo maximum · PDF de 5 pages maximum. Les images sont réduites à 2 mégapixels pour l’analyse. Sur PDF, le texte lisible est extrait directement ; les pages sans texte lisible passent par l’OCR.</p>
    <label className="studio-field">Fichier de la copie
      <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={choose} disabled={busy} />
    </label>
    {file && <p>{file.name} · {(file.size / 1024 / 1024).toFixed(2)} Mo</p>}
    <div className="studio-actions">
      <button type="button" className="studio-primary" onClick={extract} disabled={!file || busy}>Extraire localement</button>
      {busy && <button type="button" onClick={cancel}>Annuler l’extraction</button>}
    </div>
    <p role="status" aria-live="polite">{status}</p>
    {error && <p role="alert">{error}</p>}
    <label className="studio-field">Texte de la copie à relire et corriger
      <textarea rows={12} maxLength={OCR_LIMITS.characters} value={text} onChange={e => setText(e.target.value)} disabled={busy} placeholder="Le texte extrait apparaîtra ici. Vous pouvez aussi coller une transcription." />
    </label>
    <p>Ce texte ne remplace pas les observations de l’enseignant et ne déclenche aucune analyse IA. Il n’est pas enregistré automatiquement. Le texte est effacé en quittant cet onglet ou en changeant d’espace.</p>
    <div className="studio-actions">
      <button type="button" onClick={copy} disabled={!text.trim() || busy}>Copier le texte</button>
      <button type="button" onClick={download} disabled={!text.trim() || busy}>Télécharger le texte</button>
      {onText && <button type="button" onClick={() => onText(text)} disabled={!text.trim() || busy}>Utiliser ce texte relu</button>}
      {text && <button type="button" onClick={() => { if (window.confirm('Effacer le texte affiché ?')) { setText(''); setStatus(''); } }} disabled={busy}>Effacer le texte</button>}
    </div>
  </section>;
}
